import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import ContactlessCard from "components/ContactlessCard";
import InfoNotice from "components/InfoNotice";
import { DEFAULT_DEBOUNCE, FORM_KEY } from "utils/constants/form";
import dynamic from "next/dynamic";
import { useResizeDetector } from "react-resize-detector";
import Button, { FULL_WIDTH_BUTTON } from "components/Form/fields/Button";
import get from "lodash/get";
import { useViewport } from "utils/context";
import {
  SPLIT_CONTENT_MAX_WIDTH,
  SPLIT_CONTENT_SECTION_MAX_WIDTH
} from "utils/constants/ui";
import Toggles from "./Toggles";
import { getCodeDownloadFilename } from "utils/contactless";
import Analytics, { QR_CODE_DOWNLOAD } from "utils/analytics";
import { getAppliedDimensions, isServer } from "utils/view";
import Loader from "components/QRCodeStyled/Loader";
import { CONTACTLESS_PREVIEW_MODE } from "constants/contactless";
import { useShortLinks } from "utils/selectors";
import QRCodeForm from "./QRCodeForm";
import { useContactlessRefreshUrl } from "utils/hooks/contactless";
import { getUUID } from "utils/uuid";
import { useDebounce } from "utils/hooks";

const QRCodeStyled = dynamic(() => import("components/QRCodeStyled"), {
  ssr: false,
  loading: Loader
});

const DEFAULT_CLASSES = {
  container:
    "flex flex-column flex-column-m flex-row-ns w-100 center z-1 items-center items-center-m items-start-ns justify-between-ns",
  left: "flex flex-column w-100 w-50-ns z-1",
  right: "w-100 w-50-ns flex flex-column z-1 ph3 ph0-ns"
};

const Content = ({ customClasses, children, hideTitle, manual = false }) => {
  const server = isServer();
  const classes = { ...DEFAULT_CLASSES, ...customClasses };

  const { values, setValues, errors } = useFormikContext();
  const { height, width, ref } = useResizeDetector();
  const [downloadCode, setDownloadCode] = useState({});
  const { isSmall } = useViewport();
  const [refreshUUID, setRefreshUUID] = useState(getUUID());

  /**
   * Refresh uuid on value change
   * Use uuid as key for QR code so that it remounts
   * But dont set in form state because we'd trigger a recursive update as the values are observed then changed
   */
  useEffect(() => {
    setTimeout(() => {
      setRefreshUUID(getUUID());
    }, 0);
  }, [useDebounce(values, DEFAULT_DEBOUNCE)]);

  useContactlessRefreshUrl();
  const { appliedWidth } = getAppliedDimensions({
    width,
    height,
    widthFactor: isSmall ? 0.8 : 0.9,
    heightFactor: 0.75
  });

  const { [FORM_KEY]: formProps, ...qrCodeLibProps } = values;
  const qrCodeUrl = qrCodeLibProps.url;
  const testMode = formProps.testMode;
  const previewMode = formProps.previewMode;

  const urlError = get(errors, "url");
  const hasError = Boolean(!qrCodeUrl || urlError);
  const {
    staticShortLink,
    shortId,
    contactless,
    codeSizeFactor,
    widthFactor,
    heightFactor
  } = formProps;
  const shortLinks = useShortLinks();
  const shortLink =
    staticShortLink || shortLinks.find(({ short_id }) => short_id === shortId);

  const downloadQRCode = () => {
    if (downloadCode && downloadCode.code) {
      const analyticsInput = {
        url: qrCodeUrl,
        test: testMode
      };

      Analytics.track(QR_CODE_DOWNLOAD, analyticsInput);
      downloadCode.code.download({
        extension: formProps.extension,
        name: getCodeDownloadFilename({
          test: testMode,
          name: formProps.name
        })
      });
    }
  };

  const codeProps = {
    key: `${server}-${refreshUUID}`,
    download: false,
    test: testMode,
    hoistCode: setDownloadCode,
    ...qrCodeLibProps,
    error: urlError
  };

  if (formProps.withRedirect) {
    codeProps.withRedirect = true;
  }
  if (formProps.manual) {
    codeProps.manual = true;
  }

  const contactlessProps = {
    shortLink,
    contactless,
    codeSizeFactor,
    widthFactor,
    heightFactor,
    code: codeProps,
    test: testMode
  };
  const previewCodeMode = previewMode === CONTACTLESS_PREVIEW_MODE.CODE;

  const scanInfoNote = (
    <InfoNotice icon="scan" copy="Scan with your phone to test checkout." />
  );
  let content = null;

  const mobileStyledProps = {
    ...codeProps,
    minHeight: appliedWidth * 0.7,
    size: appliedWidth * 0.7
  };
  const mobileQRCode = <QRCodeStyled {...mobileStyledProps} />;

  /**
   * Only render the content when there is a width
   */
  if (appliedWidth) {
    if (previewCodeMode) {
      const styledProps = {
        ...codeProps,
        minHeight: appliedWidth,
        size: appliedWidth
      };
      const styledQRCode = <QRCodeStyled {...styledProps} />;
      content = (
        <div
          className="flex flex-column items-center justify-center w-100 relative tc"
          style={{
            minHeight: appliedWidth,
            minHeight: appliedWidth
          }}
        >
          {styledQRCode}
          <div className="pt3">{scanInfoNote}</div>
        </div>
      );
    } else if (shortLink && !manual) {
      content = (
        <ContactlessCard {...contactlessProps}>
          {contactless ? <div className="pt3">{scanInfoNote}</div> : null}
        </ContactlessCard>
      );
    }
  }

  const colorPickerProps = {
    customClasses: {
      container:
        "flex flex-row justify-between items-center relative pv3 w-100",
      label: "f6",
      description: "f6 pt2"
    },
    modalChildren: mobileQRCode
  };

  return (
    <div
      className={classes.container}
      style={{ maxWidth: SPLIT_CONTENT_MAX_WIDTH }}
    >
      <div
        style={{ maxWidth: SPLIT_CONTENT_SECTION_MAX_WIDTH }}
        className={classes.left}
      >
        {content}
        {!manual && <Toggles />}
        <div className="w-100 flex-column bb bt-ns hairline-1 pt3 mt3-ns mb3 mb0-ns pb3 pb0-ns ph3 ph0-ns">
          <div className="flex flex-row justify-between items-center w-100">
            <Button
              onClick={downloadQRCode}
              copy="Download"
              disabled={hasError}
              classes={{
                button: FULL_WIDTH_BUTTON
              }}
            />
          </div>
        </div>
      </div>
      <div
        ref={ref}
        style={{ maxWidth: SPLIT_CONTENT_SECTION_MAX_WIDTH }}
        className={classes.right}
      >
        {typeof children === "function"
          ? children({ setValues, values })
          : children}
        {!hideTitle && <div className="f3 fw6 pb4">Design your code</div>}
        <QRCodeForm colorPickerProps={colorPickerProps} />
      </div>
    </div>
  );
};

export default Content;
